"use client";

import { useEffect, useRef } from "react";
import { useSearchParams } from "next/navigation";
import { useConfigurator } from "@/store/configurator";
import type { ConfiguratorReferenceData, ConfiguratorState, ConfiguratorString } from "@/types/configurator";

// ─── Identifier contract ────────────────────────────────────────────────────
//
// Configurator state stores SLUGS for every catalog selection:
// instrumentId, lineId, stringTypeId, scaleLengthId and colorId (set-level and
// per-string) are all slugs. This is what every step component writes and
// reads, what the URL carries, and what the price/create routes accept —
// resolveConfiguration matches a slug or a database id, and slugs stay stable
// across environments.
//
// The two exceptions, both deliberate:
//   - stringCountId is an opaque database id. It is UI-only (a "count chosen"
//     marker); the wire format sends the integer stringCount.
//   - presetId is a database id, because GaugePreset has no slug column. It is
//     validated against reference data on restore and dropped whenever the
//     configuration turns custom.
//
// Restore is the normalisation boundary: URL slugs go into state as slugs, and
// nothing downstream does an id-based lookup.

// ─── Serialization (state → URL params) ─────────────────────────────────────

export function serializeState(
  state: ConfiguratorState,
  refData: ConfiguratorReferenceData
): URLSearchParams {
  const p = new URLSearchParams();

  const instrument = refData.instruments.find((i) => i.slug === state.instrumentId);
  if (!instrument) return p;
  p.set("i", instrument.slug);

  if (state.stringCount) p.set("sc", String(state.stringCount));
  if (state.scaleLengthId) p.set("sl", state.scaleLengthId); // already a slug

  const line = refData.lines.find((l) => l.slug === state.lineId);
  if (line) {
    p.set("l", line.slug);
    const st = line.stringTypes.find((t) => t.slug === state.stringTypeId);
    if (st) p.set("st", st.slug);
  }

  if (state.presetId) p.set("p", state.presetId);

  // Per-string: s1=gaugeValue,windingType[,colorSlug]
  for (const s of state.strings) {
    const parts: string[] = [s.gaugeValue, s.windingType];
    if (s.colorId) parts.push(s.colorId); // colorId is already a slug
    p.set(`s${s.position}`, parts.join(","));
  }

  if (state.colorId) p.set("c", state.colorId); // already a slug
  if (state.isPerStringColor) p.set("psc", "1");
  if (state.quantity > 1) p.set("qty", String(state.quantity));

  return p;
}

// ─── Deserialization (URL params → dispatch calls) ───────────────────────────

type Actions = {
  setInstrument: (id: string) => void;
  setStringCount: (id: string, count: number, isNonStandard: boolean) => void;
  setScaleLength: (id: string) => void;
  setLine: (id: string) => void;
  setStringType: (id: string) => void;
  setPreset: (presetId: string, strings: ConfiguratorString[], presetName?: string | null) => void;
  setStrings: (strings: ConfiguratorString[]) => void;
  setColor: (colorId: string) => void;
  togglePerStringColor: (enabled: boolean) => void;
  setQuantity: (qty: number) => void;
};

export function restoreFromParams(
  params: URLSearchParams,
  refData: ConfiguratorReferenceData,
  actions: Actions
) {
  const iSlug = params.get("i");
  if (!iSlug) return;

  const instrument = refData.instruments.find((i) => i.slug === iSlug);
  if (!instrument) return;
  actions.setInstrument(instrument.slug);

  const scStr = params.get("sc");
  if (scStr) {
    const count = parseInt(scStr, 10);
    const opt = instrument.stringCounts.find((c) => c.count === count);
    if (opt) actions.setStringCount(opt.id, opt.count, !opt.isStandard);
  }

  const sl = params.get("sl");
  if (sl) {
    const opt = instrument.scaleLengths.find((s) => s.slug === sl);
    if (opt) actions.setScaleLength(opt.slug);
  }

  const lSlug = params.get("l");
  if (lSlug) {
    const line = refData.lines.find((l) => l.slug === lSlug);
    if (line) {
      actions.setLine(line.slug);

      const stSlug = params.get("st");
      if (stSlug) {
        const st = line.stringTypes.find((t) => t.slug === stSlug);
        if (st) actions.setStringType(st.slug);
      }
    }
  }

  // Parse per-string data: s1, s2, ... (stops at first missing position)
  const strings: ConfiguratorString[] = [];
  for (let pos = 1; pos <= 12; pos++) {
    const raw = params.get(`s${pos}`);
    if (!raw) break;
    const [gaugeValue, windingType, colorId] = raw.split(",");
    if (!/^0\.\d{3,4}$/.test(gaugeValue ?? "") || !["plain", "round_wound", "flat_wound", "half_wound"].includes(windingType ?? "")) return;
    strings.push({
      position: pos,
      gaugeValue,
      windingType: windingType as ConfiguratorString["windingType"],
      colorId: colorId ?? null,
      scaleLengthId: null,
    });
  }

  if (strings.length > 0) {
    const presetId = params.get("p");
    if (presetId) {
      const line = refData.lines.find((l) => l.slug === lSlug);
      // The same preset id is offered once per approving string type, each
      // carrying that type's construction. Match the scope when the URL names
      // one. An exact-set "Customize this set" link carries the preset but no
      // string type, so the preset's own scope supplies it — selected
      // explicitly in the wizard, where the customer can change it.
      const stSlug = params.get("st");
      const candidates = line?.presets.filter((p) => p.id === presetId && p.instrumentSlug === instrument.slug) ?? [];
      const preset = stSlug ? candidates.find((p) => p.stringTypeSlug === stSlug) : candidates[0];
      if (!preset || preset.strings.length !== strings.length || preset.strings.some((s, i) => Number(s.gaugeValue) !== Number(strings[i].gaugeValue) || s.windingType !== strings[i].windingType)) return;
      if (preset.stringTypeSlug && preset.stringTypeSlug !== stSlug) actions.setStringType(preset.stringTypeSlug);
      actions.setPreset(presetId, strings, preset.name);
    } else {
      actions.setStrings(strings);
    }
  }

  // Per-string mode goes on FIRST. SET_COLOR clears every per-string colour
  // while the mode is off, so setting the base colour before enabling the mode
  // wiped the per-string colours a shared link was carrying. With the mode on,
  // SET_COLOR only sets the base and leaves the overrides alone.
  if (params.get("psc") === "1") actions.togglePerStringColor(true);

  const colorSlug = params.get("c");
  if (colorSlug) actions.setColor(colorSlug);

  const qty = params.get("qty");
  if (qty && /^\d+$/.test(qty) && Number(qty) >= 1 && Number(qty) <= 100) actions.setQuantity(Number(qty));
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * The furthest wizard step a set of URL params gets a build to. A shared or
 * "build again" link opens there instead of on step 1. Only params that were
 * actually restored count, because restore drops anything the reference data
 * doesn't support (an unknown line simply leaves the build on step 3).
 */
export function restoredStep(params: URLSearchParams, state: ConfiguratorState): number {
  if (!params.get("i") || !state.instrumentId) return 1;
  if (params.get("c") && state.colorId && state.strings.length > 0) return 6;
  if (params.get("s1") && state.strings.length > 0) return 5;
  if (params.get("l") && state.lineId && state.stringTypeId) return 4;
  if (params.get("sc") && state.stringCountId) return 3;
  return 2;
}

export function useConfiguratorUrlSync({ onRestored }: { onRestored?: (step: number) => void } = {}) {
  const {
    state,
    referenceData,
    setInstrument,
    setStringCount,
    setScaleLength,
    setLine,
    setStringType,
    setPreset,
    setStrings,
    setColor,
    togglePerStringColor,
    setQuantity,
  } = useConfigurator();

  const searchParams = useSearchParams();

  const restoredFrom = useRef<URLSearchParams | null>(null);

  // Restore from URL once on mount (runs before user has interacted)
  useEffect(() => {
    restoreFromParams(searchParams, referenceData, {
      setInstrument,
      setStringCount,
      setScaleLength,
      setLine,
      setStringType,
      setPreset,
      setStrings,
      setColor,
      togglePerStringColor,
      setQuantity,
    });
    restoredFrom.current = searchParams.toString() ? searchParams : null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once the restore has landed in state, report how far it got (external URL -> wizard position).
  useEffect(() => {
    if (!restoredFrom.current || !state.instrumentId) return;
    const params = restoredFrom.current;
    restoredFrom.current = null;
    onRestored?.(restoredStep(params, state));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Keep URL in sync whenever state changes
  useEffect(() => {
    // Guard: state is uninitialized — restore hasn't run yet or URL was empty
    if (!state.instrumentId) return;

    const serialized = serializeState(state, referenceData);
    if (serialized.toString() === searchParams.toString()) return;

    // This is URL state, not a route transition. App Router navigation can
    // remount ConfiguratorContent while the customer is moving through the
    // wizard, which resets currentStep to Instrument after every selection.
    // History replacement keeps the shareable URL without discarding the
    // in-memory wizard position or refetching the server component tree.
    window.history.replaceState(window.history.state, "", `${window.location.pathname}?${serialized.toString()}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);
}
